import {
  PdfGen,
  PdfUnit,
  PdfSize,
  PdfPen,
  PdfColor,
  PdfPoint,
  PdfRect,
  PdfFont,
  PdfBrush,
  ETextAlign,
  PageGen,
} from '../../pdf/pdf';
import { ILayoutBaseGen } from '../layoutBase';
import { OptsBase } from '../optsBase';
import '../../pdf/draw/primitives';
import '../../pdf/draw/string';
import '../../pdf/draw/cut';
import { GUtil } from '../../pdf/gutil';
import { Random, GenData } from '@educraft/base';

/**
 * Base class for "color the multiples" exercises
 * Creates two grids: one for the exercise (numbers shuffled) and one with the solution highlighted
 */
export abstract class ColorMultiplesBase implements ILayoutBaseGen {
  constructor(protected readonly callback: (n: number) => boolean) {}

  protected abstract getLabel(data: GenData): string;

  public async generate(pdf: PdfGen, opts: OptsBase): Promise<void> {
    const page = pdf.addPage();
    const random = opts.data?.random ?? new Random();

    const margin = PdfUnit.FromMillimeters(10);

    const rect1 = new PdfRect(
      margin,
      margin,
      PdfSize.A4.width - 2 * margin,
      PdfSize.A4.height / 2 - 2 * margin
    );
    const rect2 = new PdfRect(
      margin,
      PdfSize.A4.height / 2 + margin,
      PdfSize.A4.width - 2 * margin,
      PdfSize.A4.height / 2 - 2 * margin
    );

    // Generate shuffled numbers 1-100
    const numbers = Array.from({ length: 100 }, (_, i) => i + 1);
    for (let i = numbers.length - 1; i > 0; i--) {
      const j = random.next(0, i + 1);
      [numbers[i], numbers[j]] = [numbers[j], numbers[i]];
    }

    const boxes1: MultiBoxItem[] = [];
    const boxes2: MultiBoxItem[] = [];

    const label = this.getLabel(opts.data);

    // Add label at the top
    boxes1.push({
      content: label,
      rect: { x: 0, y: -2, width: 10, height: 1 },
      outline: false,
      background: null,
    });
    boxes2.push({
      content: label,
      rect: { x: 0, y: -2, width: 10, height: 1 },
      outline: false,
      background: null,
    });

    // Create grid boxes
    const lightBlue = new PdfBrush(PdfColor.fromRgb(173, 216, 230)); // LightBlue

    for (let y = 0; y < 10; y++) {
      for (let x = 0; x < 10; x++) {
        const n = numbers[y * 10 + x];
        const nStr = n.toString();
        const rect: RelRect = { x, y, width: 1, height: 1 };
        const isMatch = this.callback(n);

        boxes1.push({ content: nStr, rect, outline: true, background: null });
        boxes2.push({ content: nStr, rect, outline: true, background: isMatch ? lightBlue : null });
      }
    }

    // Draw the boxes
    this.drawRelBoxes(page, rect1, boxes1);
    this.drawRelBoxes(page, rect2, boxes2);

    // Draw cut line
    page.drawLine(
      GUtil.cutPen,
      new PdfPoint(0, PdfSize.A4.height / 2),
      new PdfPoint(PdfSize.A4.width, PdfSize.A4.height / 2)
    );
  }

  private drawRelBoxes(g: PageGen, rect: PdfRect, items: MultiBoxItem[]): void {
    if (items.length === 0) return;

    // Calculate bounds
    const minX = Math.min(...items.map((i) => i.rect.x));
    const maxX = Math.max(...items.map((i) => i.rect.x + i.rect.width));
    const minY = Math.min(...items.map((i) => i.rect.y));
    const maxY = Math.max(...items.map((i) => i.rect.y + i.rect.height));

    const boundsWidth = maxX - minX;
    const boundsHeight = maxY - minY;

    const d = Math.min(rect.width / boundsWidth, rect.height / boundsHeight);
    const px = rect.x + (rect.width - boundsWidth * d) / 2;
    const py = rect.y + (rect.height - boundsHeight * d) / 2;

    for (const box of items) {
      const boxRect = new PdfRect(
        px + (box.rect.x - minX) * d,
        py + (box.rect.y - minY) * d,
        box.rect.width * d,
        box.rect.height * d
      );

      if (box.background) {
        g.drawRectangle(null, box.background, boxRect);
      }

      if (box.outline) {
        g.drawRectangle(GUtil.basePen, null, boxRect);
        const f = g.adjustFont(boxRect.width, [box.content], GUtil.baseFont);
        g.drawString(box.content, f, PdfBrush.Black, null, boxRect, ETextAlign.Center);
      } else {
        g.drawString(box.content, GUtil.baseFont, PdfBrush.Black, null, boxRect, ETextAlign.Center);
      }
    }
  }
}

interface RelRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

interface MultiBoxItem {
  content: string;
  rect: RelRect;
  background: PdfBrush | null;
  outline: boolean;
}

// Concrete implementations for each multiplication table

export class Vybarvi_MalaNasobilka__1 extends ColorMultiplesBase {
  constructor() {
    super((n) => n % 1 === 0 && n <= 10);
  }

  protected getLabel(data: GenData): string {
    const tpl =
      data.lang['Base.Vybarvi_MalaNasobilka'] ??
      'Vybarvi násobky čísla {0} (malá násobilka, do {1})';
    return tpl.replace('{0}', '1').replace('{1}', '10');
  }
}

export class Vybarvi_MalaNasobilka__2 extends ColorMultiplesBase {
  constructor() {
    super((n) => n % 2 === 0 && n <= 20);
  }

  protected getLabel(data: GenData): string {
    const tpl =
      data.lang['Base.Vybarvi_MalaNasobilka'] ??
      'Vybarvi násobky čísla {0} (malá násobilka, do {1})';
    return tpl.replace('{0}', '2').replace('{1}', '20');
  }
}

export class Vybarvi_MalaNasobilka__3 extends ColorMultiplesBase {
  constructor() {
    super((n) => n % 3 === 0 && n <= 30);
  }

  protected getLabel(data: GenData): string {
    const tpl =
      data.lang['Base.Vybarvi_MalaNasobilka'] ??
      'Vybarvi násobky čísla {0} (malá násobilka, do {1})';
    return tpl.replace('{0}', '3').replace('{1}', '30');
  }
}

export class Vybarvi_MalaNasobilka__4 extends ColorMultiplesBase {
  constructor() {
    super((n) => n % 4 === 0 && n <= 40);
  }

  protected getLabel(data: GenData): string {
    const tpl =
      data.lang['Base.Vybarvi_MalaNasobilka'] ??
      'Vybarvi násobky čísla {0} (malá násobilka, do {1})';
    return tpl.replace('{0}', '4').replace('{1}', '40');
  }
}

export class Vybarvi_MalaNasobilka__5 extends ColorMultiplesBase {
  constructor() {
    super((n) => n % 5 === 0 && n <= 50);
  }

  protected getLabel(data: GenData): string {
    const tpl =
      data.lang['Base.Vybarvi_MalaNasobilka'] ??
      'Vybarvi násobky čísla {0} (malá násobilka, do {1})';
    return tpl.replace('{0}', '5').replace('{1}', '50');
  }
}

export class Vybarvi_MalaNasobilka__6 extends ColorMultiplesBase {
  constructor() {
    super((n) => n % 6 === 0 && n <= 60);
  }

  protected getLabel(data: GenData): string {
    const tpl =
      data.lang['Base.Vybarvi_MalaNasobilka'] ??
      'Vybarvi násobky čísla {0} (malá násobilka, do {1})';
    return tpl.replace('{0}', '6').replace('{1}', '60');
  }
}

export class Vybarvi_MalaNasobilka__7 extends ColorMultiplesBase {
  constructor() {
    super((n) => n % 7 === 0 && n <= 70);
  }

  protected getLabel(data: GenData): string {
    const tpl =
      data.lang['Base.Vybarvi_MalaNasobilka'] ??
      'Vybarvi násobky čísla {0} (malá násobilka, do {1})';
    return tpl.replace('{0}', '7').replace('{1}', '70');
  }
}

export class Vybarvi_MalaNasobilka__8 extends ColorMultiplesBase {
  constructor() {
    super((n) => n % 8 === 0 && n <= 80);
  }

  protected getLabel(data: GenData): string {
    const tpl =
      data.lang['Base.Vybarvi_MalaNasobilka'] ??
      'Vybarvi násobky čísla {0} (malá násobilka, do {1})';
    return tpl.replace('{0}', '8').replace('{1}', '80');
  }
}

export class Vybarvi_MalaNasobilka__9 extends ColorMultiplesBase {
  constructor() {
    super((n) => n % 9 === 0 && n <= 90);
  }

  protected getLabel(data: GenData): string {
    const tpl =
      data.lang['Base.Vybarvi_MalaNasobilka'] ??
      'Vybarvi násobky čísla {0} (malá násobilka, do {1})';
    return tpl.replace('{0}', '9').replace('{1}', '90');
  }
}

export class Vybarvi_MalaNasobilka__10 extends ColorMultiplesBase {
  constructor() {
    super((n) => n % 10 === 0 && n <= 100);
  }

  protected getLabel(data: GenData): string {
    const tpl =
      data.lang['Base.Vybarvi_MalaNasobilka'] ??
      'Vybarvi násobky čísla {0} (malá násobilka, do {1})';
    return tpl.replace('{0}', '10').replace('{1}', '100');
  }
}
